import searchPillsView from './searchPillsComponent.html';
import './searchPillsComponent.css';
import { ensure } from 'components/sharpr/ensure/ensure';
import { regexQuote } from 'components/sharpr/regexQuote/regexQuote';
import { spliceOut, spliceOutWhere } from 'components/sharpr/util/spliceOut';
import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import _ from 'lodash';
import moment from 'moment';

export function searchPillsComponent() {
	return {
		bindings: {
			search: '=',
		},
		controllerAs: 'vm',
		controller: searchPillsComponentCtrl,
		template: searchPillsView,
	};
}

export class searchPillsComponentCtrl {
	constructor($scope, $element, $timeout, $q, Api, Taxonomy) {
		'ngInject';
		// save injections
		this.$scope = $scope;
		this.$element = $element;
		this.$timeout = $timeout;
		this.$q = $q;
		this.Api = Api;
		this.Taxonomy = Taxonomy;
		// autobinding
		this._processCategories = this._processCategories.bind(this);
		this._processTags = this._processTags.bind(this);
		this._processClassifications = this._processClassifications.bind(this);
		this._processUsers = this._processUsers.bind(this);
		this._processDomains = this._processDomains.bind(this);
		this.initialPills = this.initialPills.bind(this);
		this.updatePills = this.updatePills.bind(this);
		this.handleWindowKeydown = this.handleWindowKeydown.bind(this);
		this.handleOutsideClick = this.handleOutsideClick.bind(this);
		this.handleAutoWidth = this.handleAutoWidth.bind(this);
	}

	$onInit() {
		// setup empty array
		this.pills = [];
		this.suggSections = [];
		this.categoriesById = {};
		this.classificationsById = {};
		this.usersById = {};
		// get taxonomy
		this.$q
			.all([
				this.Taxonomy.getCategories().then(this._processCategories),
				this.Taxonomy.getTags().then(this._processTags),
				this.Taxonomy.getClassifications().then(this._processClassifications),
				this.Taxonomy.getPublishers().then(this._processUsers),
				this.Taxonomy.getDomains().then(this._processDomains),
			])
			.then(this.initialPills, () => {});
		this.$scope.$watch('vm.search', this.updatePills, true);
		// get handle to input for selectionStart and selectionEnd
		this.input = this.$element[0].querySelector('.input');
		// get handle to ul and typing area for clientWidth
		this.pillsList = this.$element[0].querySelector('.pills');
		this.$scope.$watch(() => this.pillsList.clientWidth, this.handleAutoWidth);
		// if a pill is clicked then backspace then delete the pill
		angular.element(window).on('keydown', this.handleWindowKeydown);
		angular.element(document).on('click', this.handleOutsideClick);
	}

	$onDestroy() {
		angular.element(window).off('keydown', this.handleWindowKeydown);
		angular.element(document).off('click', this.handleOutsideClick);
		if (this.widthStyles) {
			this.widthStyles.parentNode.removeChild(this.widthStyles);
		}
	}

	handleAutoWidth() {
		const width = Math.max(this.pillsList.clientWidth + 45, 450);
		const css = `
			sh-search-pills,
			sh-search-pills .inner,
			sh-search-pills .typing-area,
			sh-search-pills .pill-sugg-area {
				width: ${width}px;
			}
		`;
		if (this.widthStyles) {
			this.widthStyles.textContent = css;
		} else {
			this.widthStyles = AssetManager.style(css);
		}
	}

	focusInput(evt) {
		evt.stopImmediatePropagation();
		this.backspacedPill = null;
		this.input.focus();
	}

	initialPills() {
		this.hasTaxonomyData = true;
		this.pills = this.getPills();
	}

	getTermAsArray() {
		return ensure
			.string(this.search.term)
			.split('&&')
			.map(t => t.trim())
			.filter(Boolean);
	}

	getPills() {
		if (!this.search || !this.hasTaxonomyData) {
			return [];
		}
		this.navPos = -1;
		const pills = [];
		this.getTermAsArray().forEach(term => {
			pills.push({
				label: term,
				tooltip: `Matching the term ${term}`,
				remove: () => {
					const terms = this.getTermAsArray();
					spliceOut(terms, term);
					this.search.term = terms.join('&&');
				},
				active: () => this.search.term == term,
			});
		});
		ensure.array(this.search.tags).forEach(tag => {
			if (tag == '(Untagged)') {
				return;
			}
			pills.push({
				label: tag,
				tooltip: `Tagged with ${tag}`,
				remove: () => spliceOut(this.search.tags, tag),
				active: () => this.search.tags.indexOf(tag) > -1,
			});
		});
		ensure.array(this.search.categories).forEach(categoryId => {
			if (categoryId === '0') {
				pills.push({
					label: '(Uncategorized)',
					tooltip: 'Posts that have no category',
					remove: () => this.looseSpliceOut(this.search.categories, categoryId),
					active: () => this.isIdInField('categories', categoryId),
				});
				return;
			}
			const category = this.categoriesById[categoryId];
			if (!category) {
				console.error(`No category found with id ${JSON.stringify(categoryId)}`);
				return;
			}
			pills.push({
				label: category.label,
				tooltip: `Categorized under ${category.label}`,
				remove: () => this.looseSpliceOut(this.search.categories, categoryId),
				active: () => this.isIdInField('categories', categoryId),
			});
		});
		ensure.array(this.search.classifications).forEach(classificationId => {
			const classification = this.classificationsById[classificationId];
			if (!classification) {
				console.error(`No classification found with id ${JSON.stringify(classificationId)}`);
				return;
			}
			pills.push({
				label: classification.label,
				tooltip: `Classified as ${classification.label}`,
				remove: () => this.looseSpliceOut(this.search.classifications, classificationId),
				active: () => this.isIdInField('classifications', classificationId),
			});
		});
		ensure.array(this.search.domains).forEach(domainName => {
			pills.push({
				label: domainName,
				tooltip: `from ${domainName}`,
				remove: () => spliceOut(this.search.domains, domainName),
				active: () => this.search.domains.indexOf(domainName) > -1,
			});
		});
		ensure.array(this.search.publishers).forEach(userId => {
			const publisher = this.usersById[userId];
			if (!publisher) {
				console.error(`No user found with id ${JSON.stringify(userId)}`);
				return;
			}
			pills.push({
				label: publisher.label,
				tooltip: `Insight by ${publisher.label}`,
				remove: () => this.looseSpliceOut(this.search.publishers, userId),
				active: () => this.isIdInField('publishers', userId),
			});
		});
		if (this.search.datePreset == 'all') {
			// skip all other date conditions
		} else if (this.search.datePreset != 'custom') {
			const name = this.search.datePreset;
			pills.push({
				label: name,
				tooltip: 'Posts published ' + name,
				remove: () => (this.search.datePreset = 'all'),
				active: () => this.search.datePreset == name,
			});
		} else if (this.search.dateStart && this.search.dateEnd) {
			const start = this.search.dateStart;
			const end = this.search.dateEnd;
			const from = moment(this.search.dateStart).format('M/D/YY');
			const to = moment(this.search.dateEnd).format('M/D/YY');
			pills.push({
				label: `${from}—${to}`,
				tooltip: `Posts published between ${from} and ${to}`,
				remove: () => (this.search.dateStart = this.search.dateEnd = undefined),
				active: () => this.search.dateStart != start || this.search.dateEnd != end,
			});
		} else if (this.search.dateStart) {
			const start = this.search.dateStart;
			const from = moment(this.search.dateStart).format('M/D/YY');
			pills.push({
				label: `After ${from}`,
				tooltip: `Posts published after ${from}`,
				remove: () => (this.search.dateStart = undefined),
				active: () => this.search.dateStart != start,
			});
		} else if (this.search.dateEnd) {
			const end = this.search.dateEnd;
			const to = moment(this.search.dateEnd).format('M/D/YY');
			pills.push({
				label: `Before ${to}`,
				tooltip: `Posts published before ${to}`,
				remove: () => (this.search.dateEnd = undefined),
				active: () => this.search.dateEnd != end,
			});
		}
		if (this.search.clientIntel === true) {
			pills.push({
				label: 'For Clients',
				tooltip: 'Posts visible to Clients',
				remove: () => (this.search.clientIntel = false),
				active: () => this.search.clientIntel === true,
			});
		}
		if (this.search.wasEmailed === true) {
			pills.push({
				label: 'Was Emailed',
				tooltip: 'Posts that have been emailed',
				remove: () => (this.search.wasEmailed = undefined),
				active: () => this.search.wasEmailed === true,
			});
		}
		if (this.search.wasEmailed === false) {
			pills.push({
				label: 'Never Emailed',
				tooltip: 'Posts that have never been emailed',
				remove: () => (this.search.wasEmailed = undefined),
				active: () => this.search.wasEmailed === false,
			});
		}
		if (this.search.inStory === true) {
			pills.push({
				label: 'In story',
				tooltip: 'Posts that are in one or more stories',
				remove: () => (this.search.inStory = undefined),
				active: () => this.search.inStory === true,
			});
		}
		if (this.search.inStory === false) {
			pills.push({
				label: 'Not in Story',
				tooltip: 'Posts that are not in any stories',
				remove: () => (this.search.inStory = undefined),
				active: () => this.search.inStory === false,
			});
		}
		if (this.search.untagged) {
			pills.push({
				label: 'Untagged',
				tooltip: 'Posts that have no tags',
				remove: () => {
					this.search.untagged = undefined;
					spliceOut(this.search.tags, '(Untagged)');
				},
				active: () => this.search.untagged,
			});
		}
		if (this.search.noInsight) {
			pills.push({
				label: 'No Insight',
				tooltip: 'Posts that have no insight text',
				remove: () => (this.search.noInsight = undefined),
				active: () => this.search.noInsight,
			});
		}
		if (this.search.approvalStatus) {
			pills.push({
				label: this.search.approvalStatus == 'approved' ? 'Approved' : 'Unapproved',
				tooltip: 'Posts that have ' + (this.search.approvalStatus == 'approved' ? ' been approved' : 'not been approved'),
				remove: () => (this.search.approvalStatus = undefined),
				active: () => this.search.approvalStatus,
			});
		}
		return pills;
	}

	updatePills() {
		if (!this.pills) {
			return;
		}
		// check for those to remove
		const keep = this.pills.filter(pill => pill.active());
		// get all applicable pills
		const all = this.getPills();
		// add pills that are not already there
		all.forEach(pill => {
			const hasDupe = _.findIndex(keep, { tooltip: pill.tooltip }) > -1;
			if (!hasDupe) {
				keep.push(pill);
			}
		});
		this.pills = keep;
	}

	selectPill(evt, pill) {
		evt.stopImmediatePropagation();
		this.backspacedPill = pill;
	}

	removePill(pill) {
		pill.remove();
		spliceOut(this.pills, pill);
		this.$timeout(() => {
			// if we don't unset backpacedPill,
			// clicking the X on a pill makes the next pill get selected
			this.backspacedPill = null;
		});
	}

	clearPills() {
		this.pills.forEach(pill => pill.remove());
		this.input.value = '';
		this.pills = [];
		this.suggSections = [];
	}

	handleInput(evt) {
		// enter, backspace, up, down
		if ([13, 8, 38, 40].indexOf(evt.keyCode) == -1) {
			this.backspacedPill = null;
			this.getResults(evt.target.value);
		}
	}

	handleControlKeys(evt) {
		if (evt.keyCode == 13) {
			this.handleEnter();
		} else if (evt.keyCode == 8) {
			this.handleBackspace();
		} else if (evt.keyCode == 38) {
			evt.preventDefault();
			this.navUp();
		} else if (evt.keyCode == 40) {
			evt.preventDefault();
			this.navDown();
		}
	}

	handleEnter() {
		if (this.selectedChoice) {
			this.useSuggestion(this.selectedChoice);
			this.selectedChoice = null;
		} else if (this.search.term) {
			this.search.term += '&&' + this.input.value;
		} else {
			this.search.term = this.input.value;
		}
		this.input.value = '';
		this.suggSections = [];
		this.$scope.$broadcast('sh-input-autowidth:adjust');
	}

	handleWindowKeydown(evt) {
		if (
			// if we have not selected a pill, we have nothing to delete
			this.backspacedPill &&
			// if we are already in input, the input handler will handle the event
			evt.target != this.input &&
			// respond to backspace (8) or delete (46)
			(evt.keyCode == 8 || evt.keyCode == 46)
		) {
			this.handleBackspace();
		}
		// if we have selected a pill but type ESC, deselect pill
		else if (this.backspacedPill && evt.keyCode == 27) {
			this.$timeout(() => {
				this.backspacedPill = null;
				this.suggSections = [];
			});
		}
	}

	handleOutsideClick(evt) {
		if (
			// if we have not selected a pill, we have nothing to delete
			this.backspacedPill &&
			this.isOutsideClick(evt.target)
		) {
			this.$timeout(() => {
				this.backspacedPill = null;
				this.suggSections = [];
			});
		}
	}

	isOutsideClick(target) {
		let el = target;
		while (el) {
			if (el === this.$element[0]) {
				return false;
			}
			el = el.parentNode;
		}
		return true;
	}

	navDown() {
		this.navPos++;
		this.highlightChoice();
	}

	navUp() {
		this.navPos--;
		this.highlightChoice();
	}

	highlightChoice() {
		const choices = [];
		this.suggSections.forEach(section => {
			section.items.forEach(item => {
				choices.push(item);
			});
		});
		const len = choices.length;
		if (this.navPos < 0) {
			// when on first item, UP moves to last item
			this.navPos = len - 1;
		}
		if (this.navPos >= len) {
			// when on last item, DOWN moves to first item
			this.navPos = 0;
		}
		this.selectedChoice = choices[this.navPos];
	}

	handleBackspace() {
		if (this.input.selectionStart > 0 || this.input.selectionEnd > 0) {
			this.$timeout(() => {
				this.backspacedPill = null;
				this.getResults(this.input.value);
			});
			return;
		}
		this.$timeout(() => {
			if (this.backspacedPill) {
				spliceOut(this.pills, this.backspacedPill);
				this.backspacedPill.remove();
				this.backspacedPill = null;
			} else {
				this.backspacedPill = this.pills[this.pills.length - 1];
			}
		});
	}

	ensureSearchArray(field) {
		if (!_.isArray(this.search[field])) {
			this.search[field] = [];
		}
	}

	isIdInField(field, id) {
		if (!_.isArray(this.search[field])) {
			return;
		}
		return this.search[field].indexOf(String(id)) > -1 || this.search[field].indexOf(Number(id)) > -1;
	}

	getResults(phrase) {
		this.selectedChoice = null;
		this.navPos = -1;
		if (phrase === '') {
			this.suggSections = [];
			return;
		}
		const allSections = [
			{
				label: 'Categories',
				search: this.search.categories,
				collection: this.categories,
				items: [],
			},
			{
				label: 'Tags',
				search: this.search.tags,
				collection: this.tags,
				items: [],
			},
			{
				label: 'Classifications',
				search: this.search.classifications,
				collection: this.classifications,
				items: [],
			},
			{
				label: 'Users',
				search: this.search.publishers,
				collection: this.users,
				items: [],
			},
			{
				label: 'Web Sites',
				search: this.search.domains,
				collection: this.domains,
				items: [],
			},
		];
		if (ensure.array(this.search.categories).length) {
			// don't allow choosing multiple categories
			spliceOutWhere(allSections, { label: 'Categories' });
		}
		const regexPhrase = new RegExp(`(^|.*?[\\s&@#"':.\/_-])(${regexQuote(phrase)})(.*)`, 'i');
		this.suggSections = allSections
			.map(section => {
				section.items = ensure.array(section.collection).filter(item => {
					item.selected = false;
					if (
						ensure.array(section.search).indexOf(String(item.id)) > -1 ||
						ensure.array(section.search).indexOf(Number(item.id)) > -1
					) {
						// already a search pill
						return;
					}
					const match = item.label.match(regexPhrase);
					if (match) {
						item.highlighted = `${match[1]}<strong>${match[2]}</strong>${match[3]}`;
						return true;
					}
					return false;
				});
				return section;
			})
			.filter(section => section.items.length > 0);
	}

	useSuggestion(item) {
		this.input.value = '';
		this.suggSections.length = 0;
		if (item.field == 'term') {
			this.search.term = item.id;
		} else {
			this.ensureSearchArray(item.field);
			this.search[item.field].push(item.id);
		}
	}

	_processCategories(categories) {
		this.categories = ensure
			.array(categories)
			.filter(cat => {
				return cat.is_active && cat.name != '-----';
			})
			.map(cat => {
				return {
					field: 'categories',
					id: String(cat.id),
					label: cat.name,
				};
			});
		this.categoriesById = _.indexBy(this.categories, 'id');
	}

	_processClassifications(classifications) {
		this.classifications = ensure.array(classifications).map(cls => {
			return {
				field: 'classifications',
				id: String(cls.id),
				color: cls.color,
				icon: cls.icon,
				label: cls.name,
			};
		});
		this.classificationsById = _.indexBy(this.classifications, 'id');
	}

	_processTags(tags) {
		this.tags = ensure.array(tags).map(tag => {
			return {
				field: 'tags',
				id: tag.name,
				label: tag.name,
			};
		});
	}

	_processUsers(users) {
		this.users = ensure.array(users).map(user => {
			return {
				field: 'publishers',
				id: user.id,
				label: `${user.fname_preferred || user.fname} ${user.lname_preferred || user.lname}`,
			};
		});
		this.usersById = _.indexBy(this.users, 'id');
	}

	_processDomains(domains) {
		this.domains = ensure.array(domains).map(domain => {
			return {
				field: 'domains',
				id: domain.name,
				label: domain.name,
			};
		});
	}

	looseSpliceOut(collection, numberOrString) {
		spliceOut(collection, Number(numberOrString));
		spliceOut(collection, String(numberOrString));
	}
}
